import { ThunkAction } from 'redux-thunk';
import { ActionCreator, Dispatch } from 'redux';
import {
  removeDecisionFromFolder as removeDecisionFromFolderAPI,
  createFolder as createFolderAPI,
  addDecisionToFolder as addDecisionToFolderAPI,
  addMultipleDecisionToFolder as addMultipleDecisionToFolderAPI,
  createAndAddDecisionToFolder,
} from '../../folders.api';
import {
  DECISIONS_FOLDERS_SET,
  DECISION_FOLDERS_SET,
  DECISION_FOLDER_ADD,
  DECISION_FOLDER_REMOVE,
  FOLDER_CREATE_ERROR_ALREADY_CREATED,
  MULTIPLE_DECISION_FOLDER_ADD,
} from '../action-types';
import { folderCreated } from './folder';
import { Folder } from '../../folders.types';

export const setFoldersForDecision = (docId: string, folders: Folder[]) =>
  <const>{
    type: DECISION_FOLDERS_SET,
    payload: { docId, folders },
  };

export const storeFolderError = () =>
  <const>{
    type: FOLDER_CREATE_ERROR_ALREADY_CREATED,
  };

export const setFoldersForDecisions = (decisionsFolders: {
  [k: string]: Array<string>;
}) =>
  <const>{
    type: DECISIONS_FOLDERS_SET,
    payload: { decisionsFolders },
  };

export const decisionFolderAdded = (
  docId: string,
  folderKey: string,
  source: string,
  searchActive: boolean,
) =>
  <const>{
    type: DECISION_FOLDER_ADD,
    payload: { docId, folderKey },
    analytics: {
      type: 'Folder DecisionAdded',
      data: {
        folder_key: folderKey,
        doc_id: docId,
        source,
        reportOriginalQueryKey: true,
        searchActive,
      },
    },
  };

export const decisionFolderRemoved = (
  docId: string,
  folderKey: string,
  searchActive: boolean,
) =>
  <const>{
    type: DECISION_FOLDER_REMOVE,
    payload: { docId, folderKey },
    analytics: {
      type: 'Folder DecisionRemoved',
      data: {
        folder_key: folderKey,
        doc_id: docId,
        searchActive,
      },
    },
  };

export const multipleDecisionFolderAdded = (
  docIds: string[],
  folderKey: string,
  source: string,
  searchActive: boolean,
) =>
  <const>{
    type: MULTIPLE_DECISION_FOLDER_ADD,
    payload: { docIds, folderKey },
    analytics: {
      type: 'Folder MultipleDecisionAdded',
      data: {
        folder_key: folderKey,
        doc_ids: docIds,
        source,
        reportOriginalQueryKey: true,
        searchActive,
      },
    },
  };

export const addDecisionFolder = (
  docId: string,
  folderKey: string,
  source: string,
  searchActive: boolean = false,
) => async (dispatch: Function) => {
  dispatch(decisionFolderAdded(docId, folderKey, source, searchActive));

  return addDecisionToFolderAPI({ docId, folderKey }).catch(error => {
    dispatch(decisionFolderRemoved(docId, folderKey, searchActive));
    return Promise.reject(error);
  });
};

export const removeDecisionFolder = (
  docId: string,
  folderKey: string,
  source: string,
  searchActive: boolean = false,
) => async (dispatch: Function) => {
  dispatch(decisionFolderRemoved(docId, folderKey, searchActive));

  return removeDecisionFromFolderAPI(docId, folderKey).catch(error => {
    dispatch(decisionFolderAdded(docId, folderKey, source, searchActive));
    return Promise.reject(error);
  });
};

export const createDecisionFolder: ActionCreator<ThunkAction<
  Promise<{ folderKey: string; title: string }>, // The type of the last action to be dispatched - will always be promise<T> for async actions
  null,
  null,
  any
>> = ({
  title,
  docId,
  source,
}: {
  title: string;
  docId: string;
  source: string;
}) => {
  return async (dispatch: Dispatch) => {
    try {
      const { folderKey } = await createAndAddDecisionToFolder({
        title,
        docId,
      });
      const folder = { folderKey, title };
      dispatch(folderCreated(folder, source));
      dispatch(decisionFolderAdded(docId, folderKey, source, false));
      return folder;
    } catch (error) {
      if (
        error.response &&
        error.response.data.error === 'FOLDER_ALREADY_EXISTS'
      )
        dispatch(storeFolderError());

      throw error;
    }
  };
};

export const addMultipleDecisionToFolder = (
  docIds: string[],
  folderKey: string,
  source: string,
  searchActive: boolean = false,
) => async (dispatch: Function) => {
  await addMultipleDecisionToFolderAPI({ docIds, folderKey });
  dispatch(
    multipleDecisionFolderAdded(docIds, folderKey, source, searchActive),
  );
};

export const addMultipleDecisionToNewFolder = ({
  docIds,
  title,
  source,
}: {
  docIds: string[];
  title: string;
  source: string;
}) => async (dispatch: Function) => {
  const folderKey = await createFolderAPI(title);
  await dispatch(folderCreated({ title, folderKey }, source));

  await dispatch(addMultipleDecisionToFolder(docIds, folderKey, source));
  return { title, folderKey };
};

export type DecisionActions = ReturnType<
  | typeof setFoldersForDecision
  | typeof storeFolderError
  | typeof decisionFolderAdded
  | typeof decisionFolderRemoved
  | typeof setFoldersForDecisions
  | typeof multipleDecisionFolderAdded
>;
